<template>
    <CentralPanel
        class="referendum-infos"
        :class="{ engine: isEngine }"
        :style="{ minHeight: panelMinHeight }"
        :title="isEngine && engine.terms.length > 0 ? undefined : 'Referendum'"
    >
        <div
            v-if="isEngine && engine.terms.length > 0"
            class="referendum-terms"
        >
            <!-- The title on the first line ( the card, who calls it ) -->
            <div
                v-for="(line, i) in engine.terms"
                :key="i"
            >
                <span
                    v-if="i == 0"
                    class="central-panel-title"
                    >Referendum :
                </span>
                <EngineText :text="line" />
            </div>
        </div>
        <div
            v-if="isEngine && engine.referendumText"
            ref="cardText"
            class="referendum-card-text"
            :style="{ clipPath: `inset(0 0 ${cardTextClip}px 0)` }"
            @scroll="fitCardText"
        >
            {{ engine.referendumText }}
        </div>
        <div class="referendum-body">
            <!-- With the rules engine : on one line, the Methuselahs who cast such a vote ( the Edge, a card ) -->
            <div
                v-if="isEngine && castVoteRows.length > 0"
                class="methuselah-votes-line"
            >
                <span class="methuselah-votes-title">Methuselah votes :</span>
                <span
                    v-for="row in castVoteRows"
                    :key="row.player.oid"
                    class="methuselah-votes-cast"
                >
                    <span
                        class="inline-player-name"
                        :style="{ backgroundColor: row.player.rgbaColor }"
                    >
                        {{ row.player.shortName }}
                    </span>
                    <span
                        v-if="row.votes[VoteSide.InFavour] > 0"
                        class="in-favour"
                    >
                        {{ row.votes[VoteSide.InFavour] }} in favour
                    </span>
                    <span
                        v-if="row.votes[VoteSide.Against] > 0"
                        class="against"
                    >
                        {{ row.votes[VoteSide.Against] }} against
                    </span>
                </span>
            </div>

            <!--
            Left column : votes that don't come from a vampire, one row per
            Methuselah. Only your own row is editable, everyone announces their
            own. Its width is content-driven and stable, which is what keeps the
            tally on the right from moving as the panel changes state.
            -->
            <div
                v-if="!isEngine"
                class="methuselah-votes"
            >
                <span class="methuselah-votes-title">Methuselah votes</span>

                <span class="methuselah-votes-header">In favour</span>
                <span class="methuselah-votes-header">Against</span>

                <template
                    v-for="row in methuselahVoteRows"
                    :key="row.player.oid"
                >
                    <span
                        class="inline-player-name methuselah-votes-player"
                        :style="{ backgroundColor: row.player.rgbaColor }"
                    >
                        {{ row.player.shortName }}
                    </span>

                    <span class="methuselah-votes-cell">
                        <PropertyStepper
                            v-if="row.isSelf"
                            :readOnly="isEngine"
                            :value="row.votes[VoteSide.InFavour]"
                            @change="changePlayerVotes(row.player, VoteSide.InFavour, $event)"
                        />

                        <template v-else>{{ row.votes[VoteSide.InFavour] }}</template>
                    </span>

                    <span class="methuselah-votes-cell">
                        <PropertyStepper
                            v-if="row.isSelf"
                            :readOnly="isEngine"
                            :value="row.votes[VoteSide.Against]"
                            @change="changePlayerVotes(row.player, VoteSide.Against, $event)"
                        />

                        <template v-else>{{ row.votes[VoteSide.Against] }}</template>
                    </span>
                </template>
            </div>

            <!-- Right column : the tally, the panel's headline, and the controls -->
            <div class="referendum-summary">
                <div class="referendum-tally">
                    <div
                        class="tally-side in-favour"
                        :class="{ 'is-winner': winningSide == VoteSide.InFavour }"
                    >
                        <span class="tally-amount">{{ tally[VoteSide.InFavour] }}</span>
                        <span class="tally-label">In favour</span>
                    </div>

                    <div
                        class="tally-side against"
                        :class="{ 'is-winner': winningSide == VoteSide.Against }"
                    >
                        <span class="tally-amount">{{ tally[VoteSide.Against] }}</span>
                        <span class="tally-label">Against</span>
                    </div>
                </div>

                <!--
                Priscii subreferendum : always on the panel so the room is
                reserved, whether ballots were cast or not. Once a side carries
                it, its count is highlighted and the grant it earns is spelled
                out at the end of the line.
                -->
                <!-- With the rules engine : only with a priscus at the table -->
                <div
                    v-if="!isEngine || prisciAtTable"
                    class="subreferendum"
                >
                    <span class="subreferendum-title">Priscii</span>

                    <span
                        class="subreferendum-side in-favour"
                        :class="{ 'is-winner': subWinner == VoteSide.InFavour }"
                    >
                        {{ subTally[VoteSide.InFavour] }} in favour
                    </span>

                    <span
                        class="subreferendum-side against"
                        :class="{ 'is-winner': subWinner == VoteSide.Against }"
                    >
                        {{ subTally[VoteSide.Against] }} against
                    </span>

                    <span
                        v-if="subWinner"
                        class="subreferendum-grant"
                    >
                        Grants {{ SUBREFERENDUM_VOTE_GRANT }}
                    </span>
                </div>

                <div
                    v-if="!isEngine"
                    class="referendum-controls"
                >
                    <!--
                    Fixed slots : the Interrupt button takes the place of the
                    Last call button, and the countdown takes the room between
                    the two buttons, so nothing moves when a last call starts
                    or stops.
                    -->
                    <template v-if="lastCallPending">
                        <button
                            class="game-button small"
                            @click="gameMutations.REFERENDUM_interruptLastCall.actSelf({})"
                        >
                            Interrupt
                        </button>
                    </template>

                    <button
                        v-else
                        class="game-button small"
                        @click="startLastCall"
                    >
                        Last call
                    </button>

                    <span
                        v-if="lastCallPending"
                        class="last-call-countdown"
                    >
                        Last call : {{ remainingSeconds }}
                    </span>

                    <button
                        class="game-button small is-danger"
                        @click="gameMutations.REFERENDUM_close.actSelf({})"
                    >
                        End referendum
                    </button>
                </div>
            </div>
        </div>
    </CentralPanel>
</template>

<script setup lang="ts">
/**
 * Tally of an ongoing referendum.
 *
 * The panel is a scoreboard, not a referee : votes are announced by the players
 * through the vote boxes sitting next to their vampires, and through the
 * Methuselah vote steppers here for everything that has no vampire behind it
 * ( burning the edge, discarding a political card, activating a card in play ).
 * The only thing settled here is the last call, and even that is only a display
 * : the countdown runs off the shared lastCallStartTime, so each client reaches
 * the same outcome without any further broadcast.
 *
 * The whole panel has to fit the central box, which is short, hence the two
 * columns : one row per Methuselah on the left, the tally and its controls on
 * the right.
 */
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { useGameStateStore } from '@/client/store/gameState.ts'
import { usePlayersStore } from '@/client/state/players.ts'
import { gameMutations } from '@/shared/state/gameMutations.ts'
import { GameType, ReferendumState, VoteSide } from '@/shared/types/state.ts'
import {
    getLastCallRemainingTime,
    getPlayerVotes,
    isBallotVampire,
    getSubReferendumWinner,
    getWinningSide,
    isLastCallOver,
    SUBREFERENDUM_VOTE_GRANT,
    tallySubReferendum,
    tallyVotes,
} from '@/shared/state/referendumState.ts'
import { Player } from '@/shared/model/Player.ts'
import PropertyStepper from '@/client/ui/components/PropertyStepper.vue'
import CentralPanel from '@/client/ui/ingame/topArea/central/CentralPanel.vue'
import { useEngineStore } from '@/client/store/engine.ts'
import EngineText from '@/client/ui/ingame/EngineText.vue'

const props = defineProps<{
    referendum: ReferendumState
}>()

const gameState = useGameStateStore()
// A game with the rules engine : the engine counts the votes ( see engine/projection.ts )
const isEngine = computed(() => gameState.gameType == GameType.Engine)
const engine = useEngineStore()
const players = usePlayersStore()

/**
 * Last call countdown
 *
 * We only tick while a countdown is actually running, and stop as soon as it
 * is over : there is nothing left to refresh once the outcome is settled.
 */

const LAST_CALL_TICK = 100

const now = ref(Date.now())
let tickInterval: ReturnType<typeof setInterval> | undefined

function stopTick() {
    clearInterval(tickInterval)
    tickInterval = undefined
}

watch(
    () => props.referendum.lastCallStartTime,
    lastCallStartTime => {
        stopTick()
        now.value = Date.now()

        if (lastCallStartTime === null) {
            return
        }

        tickInterval = setInterval(() => {
            now.value = Date.now()
            if (isLastCallOver(props.referendum, now.value)) {
                stopTick()
            }
        }, LAST_CALL_TICK)
    },
    { immediate: true },
)

onUnmounted(stopTick)

const outcomeReached = computed(() => isLastCallOver(props.referendum, now.value))

const lastCallPending = computed(
    () => props.referendum.lastCallStartTime !== null && !outcomeReached.value,
)

const remainingSeconds = computed(() =>
    Math.ceil(getLastCallRemainingTime(props.referendum, now.value) / 1000),
)

function startLastCall() {
    gameMutations.REFERENDUM_startLastCall.actSelf({ date: new Date() })
}

const tally = computed(() => tallyVotes(props.referendum))

// Nothing is highlighted until an uninterrupted last call settles the outcome
const winningSide = computed(() => (outcomeReached.value ? getWinningSide(props.referendum) : null))

/**
 * Priscii subreferendum
 *
 * Priscii cast ballots into a subreferendum of their own, whose winning side
 * then grants a fixed number of votes to the main tally ( none on a tie ). The
 * block only appears once at least one ballot has been cast, since there is no
 * subreferendum to speak of otherwise. The grant is already folded into the
 * main tally by tallyVotes, so this only spells out where those votes came from.
 */

const subTally = computed(() => tallySubReferendum(props.referendum))
// With the rules engine, the rows and lines shown only when they hold something
const castVoteRows = computed(() =>
    methuselahVoteRows.value.filter(
        row => row.votes[VoteSide.InFavour] > 0 || row.votes[VoteSide.Against] > 0,
    ),
)
const prisciAtTable = computed(() =>
    players.orderedPlayers.some(player => player.vampiresReady.some(isBallotVampire)),
)
const subWinner = computed(() => getSubReferendumWinner(props.referendum))

/**
 * Methuselah votes
 *
 * Every player gets a row, whether they brought votes or not, so the table
 * reads as the standing of the whole table rather than as a growing list.
 */

const methuselahVoteRows = computed(() =>
    gameState.competingPlayers.map(player => ({
        player,
        votes: getPlayerVotes(props.referendum, player),
        isSelf: player == players.selfPlayer,
    })),
)

function changePlayerVotes(player: Player, side: VoteSide, amount: number) {
    gameMutations.REFERENDUM_changePlayerVotes.actSelf({ player, side, amount })
}

/**
 * With the rules engine : the card's text gives way when the central box is short ( see the
 * style ), down to none, the panel's other rows whole ( its least height ), whole lines only ( the
 * line its room or its scroll cuts at the bottom hidden )
 */
const cardText = ref<HTMLElement | null>(null)
const cardTextClip = ref(0)
const panelMinHeight = ref<string | undefined>(undefined)

function fitCardText() {
    const text = cardText.value
    const panel = text?.parentElement
    if (!text || !panel) {
        panelMinHeight.value = undefined
        cardTextClip.value = 0
        return
    }
    panelMinHeight.value = `${panel.scrollHeight - text.offsetHeight}px`
    const line = parseFloat(getComputedStyle(text).lineHeight)
    cardTextClip.value = line > 0 ? (text.clientHeight + text.scrollTop) % line : 0
}

// The text's room and the panel's rows ( terms, votes ) change : fitted again
const cardTextResizes = new ResizeObserver(fitCardText)
watch(cardText, text => {
    cardTextResizes.disconnect()
    if (text) {
        cardTextResizes.observe(text)
        if (text.parentElement) {
            cardTextResizes.observe(text.parentElement)
        }
    }
    fitCardText()
})
onUnmounted(() => cardTextResizes.disconnect())
// A row changed while the panel's height and the text's stay put ( the panel shrunk, the text
// gone ) : no resize to tell it
watch([() => engine.terms, () => engine.referendumText, castVoteRows, prisciAtTable], () =>
    nextTick(fitCardText),
)
</script>

<style lang="scss">
.central-panel.referendum-infos {
    // What passing does, under the card's name
    .referendum-card-text {
        font-size: 12px;
        font-style: italic;
        opacity: 0.85;
        max-width: 32rem;
    }

    // The panel's own dark text, a little larger than its dense table
    .referendum-terms {
        font-size: 14px;
        text-align: center;
        margin-bottom: 4px;
    }

    // The central box is short, so this panel stays deliberately dense
    font-size: 13px;
    padding: 0.5rem 0.75rem;

    .referendum-body {
        align-self: stretch;
        display: flex;
        gap: 1rem;
    }

    /**
     * Methuselah votes
     */

    .methuselah-votes {
        // Content-sized and stable : the tally takes whatever is left, so
        // nothing in this panel reflows as its state changes
        flex: 0 0 auto;
        display: grid;
        // Fixed vote columns so a count growing to two digits doesn't widen
        // the table, and so plain numbers line up under the headers
        grid-template-columns: auto 70px 70px;
        align-items: center;
        gap: 1px 0.4rem;
    }

    // Plain case : uppercased, this heading widened the whole table
    .methuselah-votes-title {
        font-size: 12px;
    }

    .methuselah-votes-header {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: center;
    }

    .methuselah-votes-player {
        justify-self: start;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .methuselah-votes-cell {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.2rem;
        font-weight: bold;
        font-variant-numeric: tabular-nums;
    }

    /**
     * Tally and controls
     */

    // Ruled off from the table : the tally on top, its controls underneath
    .referendum-summary {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 0.4rem;
        padding-left: 1rem;
        border-left: solid 1px rgba($shadow-grey, 0.4);
    }

    .referendum-tally {
        display: flex;
        justify-content: center;
        gap: 0.5rem;
    }

    .tally-side {
        // The headline of the whole panel : both sides share the space the
        // table left over, and take as much of it as they can
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 2px 4px;
        border: solid 2px transparent;

        .tally-amount {
            font-size: 48px;
            font-weight: bold;
            line-height: 1.05;
            font-variant-numeric: tabular-nums;
        }

        .tally-label {
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        &.in-favour .tally-amount {
            color: $dark-forest;
        }

        &.against .tally-amount {
            color: $wine-crimson;
        }

        // The side that carried an uninterrupted last call
        &.is-winner {
            border-color: $shadow-grey;
            background: rgba($ghost-white, 0.9);
        }
    }

    /**
     * Priscii subreferendum
     */

    // The whole subreferendum on a single dense line : label, then both counts
    .subreferendum {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 2px 4px;
        border: solid 1px $royal-purple;
    }

    .subreferendum-title {
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: $royal-purple;
    }

    .subreferendum-side {
        font-size: 12px;
        font-weight: bold;
        font-variant-numeric: tabular-nums;
        padding: 0 3px;
        border: solid 1px transparent;

        &.in-favour {
            color: $dark-forest;
        }

        &.against {
            color: $wine-crimson;
        }

        // The side that carried the subreferendum, and so granted its votes
        &.is-winner {
            border-color: $royal-purple;
        }
    }

    // The votes the winning side earns, tacked onto the end of the line
    .subreferendum-grant {
        font-size: 11px;
        font-weight: bold;
        color: $royal-purple;
    }

    // Both controls share one width, at the right edge of the panel
    .referendum-controls {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.5rem;

        > * {
            width: 100px;
        }
    }

    // The one time-critical thing on the panel, hence the size and the colour
    .last-call-countdown {
        // Content-sized : sitting between the two fixed control slots, its own
        // width changes nothing around it
        width: auto;
        font-size: 17px;
        font-weight: bold;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        text-align: center;
        color: $blood-red;
        animation: ReferendumLastCallAppear 0.3s ease-out;
    }
}

// With the rules engine the central box holds the decision and the cards played too, and is
// short in a window the table's height bounds ( TOP_AREA_HEIGHT ) : the totals beside the
// terms, the votes and the priscii on one line, the card's text the one row that gives way ( its
// name shows the card on hover )
.central-panel.referendum-infos.engine {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto minmax(0, auto) auto;
    grid-template-areas:
        'terms tally'
        'text text'
        'votes sub';
    align-items: center;
    column-gap: 0.75rem;
    row-gap: 2px;
    padding: 0.25rem 0.75rem;

    .referendum-body,
    .referendum-summary {
        display: contents;
    }

    // The panel's own title when no terms were told
    > .central-panel-title,
    .referendum-terms {
        grid-area: terms;
        font-size: 13px;
        margin-bottom: 0;
    }

    .referendum-card-text {
        grid-area: text;
        justify-self: center;
        // Its row's height, not its own : scrolled within the room left
        align-self: stretch;
        line-height: 1.2;
        max-height: 3.6em;
        min-height: 0;
        overflow-y: auto;
        text-align: center;
    }

    // One side over the other : the terms keep the width, a line each
    .referendum-tally {
        grid-area: tally;
        flex-direction: column;
        gap: 0;
    }

    .tally-side {
        flex: 0 0 auto;
        white-space: nowrap;
        flex-direction: row;
        justify-content: flex-start;
        align-items: baseline;
        gap: 0.4rem;
        padding: 0 4px;

        .tally-amount {
            font-size: 20px;
        }
    }

    .methuselah-votes-line {
        grid-area: votes;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 2px 0.5rem;
        font-size: 12px;

        .methuselah-votes-cast {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            font-weight: bold;
        }

        .in-favour {
            color: $dark-forest;
        }

        .against {
            color: $wine-crimson;
        }
    }

    .subreferendum {
        grid-area: sub;
    }
}

@keyframes ReferendumLastCallAppear {
    0% {
        opacity: 0;
        transform: scale(0.8);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}
</style>
